import React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import { hexToRgba } from "@/utils/hexToRgba"

const chipVariants = cva(
  "inline-flex items-center rounded-md border font-medium focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "border-transparent",
      },
      size: {
        sm: "text-xs px-2 py-1",
        md: "text-sm px-3 py-1",
        lg: "text-base px-4 py-2",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  },
)

export interface ChipProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof chipVariants> {
  color?: string
  iconLeft?: React.ReactElement
  iconRight?: React.ReactElement
}

const Chip = React.forwardRef<HTMLDivElement, ChipProps>(
  ({ className, variant, size, color, iconLeft, iconRight, children, ...props }, ref) => {
    const backgroundColor = color ? hexToRgba(color, 0.1) : "transparent"
    const iconSize = size === "sm" ? 14 : size === "md" ? 16 : 20

    return (
      <div
        ref={ref}
        className={cn(chipVariants({ variant, size, className }), "gap-2")}
        style={{
          color: color,
          backgroundColor: backgroundColor,
        }}
        {...props}
      >
        {iconLeft &&
          React.cloneElement(iconLeft, {
            style: { width: iconSize, height: iconSize },
          })}
        {children}
        {iconRight &&
          React.cloneElement(iconRight, {
            style: { width: iconSize, height: iconSize },
          })}
      </div>
    )
  },
)

Chip.displayName = "Chip"

export { Chip }
